import { EmbeddingDataItem, postApiEmbeddingCollectionSearchDataItems, useGetApiEmbeddingCollectionList } from "@/API";
import {
    Badge,
    Button,
    Center,
    Checkbox,
    Group,
    Loader,
    Modal,
    Pagination,
    Paper,
    Select,
    Stack,
    Table,
    Text,
    TextInput
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconFileText, IconSearch } from "@tabler/icons-react";
import { useEffect, useState } from "react";

export function SelectDataIdsModal(props: {
    opened: boolean;
    onClose: () => void;
    workspaceId: string;
    collectionIds?: string[];
    selectedDataIds: string[];
    onConfirm: (selectedDataIds: string[], inferredCollectionIds: string[]) => void;
}) {
    const { opened, onClose, workspaceId, collectionIds, selectedDataIds, onConfirm } = props;

    const collectionsQuery = useGetApiEmbeddingCollectionList({ workspaceId, skip: 0, limit: 100 });
    const collectionsList = collectionsQuery.data?.data.results ?? [];

    const [selectedCollectionFilter, setSelectedCollectionFilter] = useState<string>("ALL");

    const [loading, setLoading] = useState(false);
    const [pageItems, setPageItems] = useState<EmbeddingDataItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch] = useDebouncedValue(searchQuery, 300);

    const [tempSelected, setTempSelected] = useState<string[]>([]);
    // Map dataId -> collectionId for inferred collections
    const [selectedCollectionMap, setSelectedCollectionMap] = useState<Map<string, string>>(new Map());

    const [page, setPage] = useState(1);
    const pageSize = 8;

    // Reset when modal opens
    useEffect(() => {
        if (opened) {
            setTempSelected(selectedDataIds ?? []);
            setSearchQuery("");
            setSelectedCollectionFilter("ALL");
            setPage(1);
        }
    }, [opened, selectedDataIds]);

    // Reset page when debounced search query or collectionIds filter changes
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, collectionIds, selectedCollectionFilter]);

    // Fetch server-side paginated & filtered data items
    useEffect(() => {
        if (!opened) return;

        let isCancelled = false;
        setLoading(true);

        const queryParams: { workspaceId: string; skip: number; limit: number; search?: string } = {
            workspaceId,
            skip: (page - 1) * pageSize,
            limit: pageSize
        };
        if (debouncedSearch.trim()) {
            queryParams.search = debouncedSearch.trim();
        }

        const effectiveCollectionIds = selectedCollectionFilter === "ALL"
            ? (collectionIds ?? [])
            : [selectedCollectionFilter];

        postApiEmbeddingCollectionSearchDataItems(
            effectiveCollectionIds,
            queryParams
        )
            .then(res => {
                if (isCancelled) return;
                const results = res.data.results ?? [];
                setPageItems(results);
                setTotalCount(res.data.total ?? 0);

                // Update selectedCollectionMap with fetched item mappings
                setSelectedCollectionMap(prev => {
                    const next = new Map(prev);
                    results.forEach(item => {
                        if (item.dataId && item.collectionId) {
                            next.set(item.dataId, item.collectionId);
                        }
                    });
                    return next;
                });
            })
            .catch(err => {
                console.error("Failed to load server-side paginated data items:", err);
            })
            .finally(() => {
                if (!isCancelled) setLoading(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [opened, workspaceId, collectionIds, selectedCollectionFilter, page, pageSize, debouncedSearch]);

    const totalPages = Math.ceil(totalCount / pageSize) || 1;

    const handleToggleItem = (item: EmbeddingDataItem) => {
        const dataId = item.dataId;
        if (!dataId) return;

        setTempSelected(prev => {
            return prev.includes(dataId)
                ? prev.filter(id => id !== dataId)
                : [...prev, dataId];
        });

        if (item.collectionId) {
            const colId = item.collectionId;
            setSelectedCollectionMap(prev => new Map(prev).set(dataId, colId));
        }
    };

    const handleSelectPageAll = () => {
        const newMap = new Map(selectedCollectionMap);
        const pageIds: string[] = [];

        pageItems.forEach(i => {
            if (i.dataId) {
                pageIds.push(i.dataId);
                if (i.collectionId) newMap.set(i.dataId, i.collectionId);
            }
        });

        setSelectedCollectionMap(newMap);
        setTempSelected(prev => Array.from(new Set([...prev, ...pageIds])));
    };

    const handleDeselectPageAll = () => {
        const pageIds = new Set(pageItems.map(i => i.dataId).filter((id): id is string => !!id));
        setTempSelected(prev => prev.filter(id => !pageIds.has(id)));
    };

    const handleClearAll = () => {
        setTempSelected([]);
    };

    const handleConfirm = () => {
        const inferredColIds = new Set<string>();
        tempSelected.forEach(dataId => {
            const colId = selectedCollectionMap.get(dataId);
            if (colId) inferredColIds.add(colId);
        });

        onConfirm(tempSelected, Array.from(inferredColIds));
        onClose();
    };

    const validPageItems = pageItems.filter((i): i is EmbeddingDataItem & { dataId: string } => !!i.dataId);
    const isAllPageSelected = validPageItems.length > 0 && validPageItems.every(i => tempSelected.includes(i.dataId));

    const collectionSelectData = [
        { value: "ALL", label: collectionIds && collectionIds.length > 0 ? `預設範圍 (${collectionIds.length} 個集合)` : "全部集合" },
        ...collectionsList.filter(c => !!c.id).map(c => ({
            value: c.id!,
            label: c.name || c.id!
        }))
    ];

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Group gap="xs">
                    <IconFileText size={20} />
                    <Text fw={600}>選擇出題特定文件檔案 (DataIds)</Text>
                    <Badge variant="filled" color="blue" size="sm">
                        已選 {tempSelected.length} 項
                    </Badge>
                </Group>
            }
            size="lg"
        >
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Select
                        w={200}
                        placeholder="選擇資料集合"
                        data={collectionSelectData}
                        value={selectedCollectionFilter}
                        onChange={(val) => setSelectedCollectionFilter(val || "ALL")}
                        allowDeselect={false}
                    />
                    <TextInput
                        flex={1}
                        placeholder="搜尋檔案名稱、DataId、標籤..."
                        leftSection={<IconSearch size={16} />}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.currentTarget.value)}
                    />
                    <Group gap="xs">
                        <Button
                            variant="light"
                            size="xs"
                            onClick={isAllPageSelected ? handleDeselectPageAll : handleSelectPageAll}
                            disabled={validPageItems.length === 0}
                        >
                            {isAllPageSelected ? "取消全選本頁" : "全選本頁"}
                        </Button>
                        <Button
                            variant="subtle"
                            color="gray"
                            size="xs"
                            onClick={handleClearAll}
                            disabled={tempSelected.length === 0}
                        >
                            清除全部
                        </Button>
                    </Group>
                </Group>

                {loading ? (
                    <Center p="xl">
                        <Stack align="center" gap="xs">
                            <Loader size="md" />
                            <Text size="sm" c="dimmed">正在查詢伺服器檔案，請稍候...</Text>
                        </Stack>
                    </Center>
                ) : validPageItems.length === 0 ? (
                    <Paper p="xl" withBorder radius="md">
                        <Center>
                            <Text size="sm" c="dimmed">
                                {totalCount === 0 ? "尚無可用的文件檔案" : "找不到符合搜尋條件的檔案"}
                            </Text>
                        </Center>
                    </Paper>
                ) : (
                    <>
                        <Table highlightOnHover withTableBorder withColumnBorders>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th style={{ width: 40 }}>
                                        <Checkbox
                                            checked={isAllPageSelected}
                                            onChange={isAllPageSelected ? handleDeselectPageAll : handleSelectPageAll}
                                        />
                                    </Table.Th>
                                    <Table.Th>文件/資料名稱</Table.Th>
                                    <Table.Th>所屬集合</Table.Th>
                                    <Table.Th style={{ width: 120 }}>標籤</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {validPageItems.map(item => {
                                    const isChecked = tempSelected.includes(item.dataId);
                                    return (
                                        <Table.Tr
                                            key={item.dataId}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleToggleItem(item)}
                                        >
                                            <Table.Td onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={isChecked}
                                                    onChange={() => handleToggleItem(item)}
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm" fw={500} lineClamp={1}>
                                                    {item.description || item.dataId}
                                                </Text>
                                                <Text size="xs" c="dimmed">
                                                    ID: {item.dataId}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge variant="outline" size="xs">
                                                    {item.collectionName || item.collectionId}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap={4}>
                                                    {item.tags?.slice(0, 2).map((t: string, idx: number) => (
                                                        <Badge key={idx} size="xs" variant="light" color="teal">
                                                            {t}
                                                        </Badge>
                                                    ))}
                                                    {(item.tags?.length ?? 0) > 2 && (
                                                        <Badge size="xs" variant="subtle" color="gray">
                                                            +{(item.tags?.length ?? 0) - 2}
                                                        </Badge>
                                                    )}
                                                </Group>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>

                        <Group justify="space-between" align="center">
                            <Text size="xs" c="dimmed">
                                顯示第 {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalCount)} 筆，共 {totalCount} 筆檔案
                            </Text>
                            <Pagination
                                size="sm"
                                value={page}
                                onChange={setPage}
                                total={totalPages}
                            />
                        </Group>
                    </>
                )}

                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={onClose}>取消</Button>
                    <Button onClick={handleConfirm}>確認選擇 ({tempSelected.length})</Button>
                </Group>
            </Stack>
        </Modal>
    );
}
